import { computed } from '@angular/core';
import { type DateRange } from '@angular/material/datepicker';
import { defaultSelectOption, type SelectExtendedOption, type SelectOption } from '@core';
import { incidentColumnOptions, IncidentTabStatusFilter } from '@models';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { AvsDateRangeIds } from '@ui';

import { type AvailableIncidentFiltersDataDto } from '../models';

export interface IncidentsFilterCriteria {
  deviceStatus?: string[];
  deviceId?: string;
  deviceTypeId?: string[];
  endDateTime?: string;
  locationId?: string;
  query?: string; // ticket-number substring search, now server-side
  severity?: string[];
  spaceId?: string;
  startDateTime?: string;
}

export interface IncidentsSortCriteria {
  field: string;
  order: 'asc' | 'desc';
}

export interface FilterOption extends SelectOption {
  id?: string;
}

export interface FilterExtendedOption extends SelectExtendedOption {
  id?: string;
}

interface IncidentsCriteriaState {
  criteria: IncidentsFilterCriteria;
  sortCriteria: IncidentsSortCriteria | null;

  tab: IncidentTabStatusFilter;
  search: string;
  displayedColumns: string[];
  columnOptions: SelectOption[];

  facets: AvailableIncidentFiltersDataDto | null;
  deviceTypeOptions: FilterExtendedOption[];
  severityOptions: FilterExtendedOption[];
  buildingOptions: FilterOption[];
  floorOptions: FilterOption[];
  roomOptions: FilterOption[];
  selectedBuilding: string;
  selectedFloor: string;
  selectedRoom: string;

  resolvedIncidentsDeviceTypeOptions: FilterExtendedOption[];
  resolvedIncidentsBuildingOptions: FilterOption[];
  resolvedIncidentsFloorOptions: FilterOption[];
  resolvedIncidentsRoomOptions: FilterOption[];
  resolvedIncidentsSelectedBuilding: string;
  resolvedIncidentsSelectedFloor: string;
  resolvedIncidentsSelectedRoom: string;

  resolvedDateRangeId: AvsDateRangeIds;
  resolvedDates: { endDateTime: string; startDateTime: string; };
}

/*
 * Fresh object + fresh arrays every call, not a shared module-level constant —
 * `reset()` calls this too, and `patchState` assigns whatever's passed by
 * reference. A shared constant would mean every reset (and every store
 * instance) pointed its "empty" array fields at the exact same `[]`
 * instances; harmless today since no method mutates an array in place
 * (all use `.map`/spread), but a single future `.push()` would corrupt the
 * template for every store past and future. Cheap to avoid outright instead
 * of relying on that discipline holding forever.
 */
function createInitialState(): IncidentsCriteriaState {
  /*
   * Default tab is Open, so the initial column list excludes 'resolved' the
   * same way `setTab` would if switching *to* Open — and, since `displayedColumns`
   * drives `matHeaderRowDef`/`matRowDef` directly (the table's static index/
   * ticketNumber columns still need to be listed there to render at all), it
   * must go through `generateTableColumns` for that prefix too, not just the
   * raw `incidentColumnOptions` value list.
   */
  const initialOpenColumns = incidentColumnOptions.filter(({ value }) => value !== 'resolved').map(option => option.value);

  return {
    buildingOptions: [],
    columnOptions: incidentColumnOptions.filter(({ value }) => value !== 'resolved'),
    criteria: {},
    deviceTypeOptions: [],
    displayedColumns: generateTableColumns(initialOpenColumns),
    facets: null,
    floorOptions: [],
    resolvedDateRangeId: AvsDateRangeIds.TODAY,
    resolvedDates: { endDateTime: '', startDateTime: '' },
    resolvedIncidentsBuildingOptions: [],
    resolvedIncidentsDeviceTypeOptions: [],
    resolvedIncidentsFloorOptions: [],
    resolvedIncidentsRoomOptions: [],
    resolvedIncidentsSelectedBuilding: '',
    resolvedIncidentsSelectedFloor: '',
    resolvedIncidentsSelectedRoom: '',
    roomOptions: [],
    search: '',
    selectedBuilding: '',
    selectedFloor: '',
    selectedRoom: '',
    severityOptions: [],
    sortCriteria: null,
    tab: IncidentTabStatusFilter.OPEN,
  };
}

function generateFloorOptions(facets: AvailableIncidentFiltersDataDto, selectedBuilding: string): FilterOption[] {
  const buildingId = facets.buildings.find(({ title }) => title === selectedBuilding)?.value;

  return [
    defaultSelectOption,
    ...facets.floors
      .filter(floor => floor.parentId === buildingId)
      .map(floor => ({ id: floor.value, title: floor.title, value: floor.title })),
  ];
}

function generateRoomOptions(facets: AvailableIncidentFiltersDataDto, selectedFloor: string): FilterOption[] {
  const floorId = facets.floors.find(({ title }) => title === selectedFloor)?.value;

  return [
    defaultSelectOption,
    ...facets.rooms
      .filter(room => room.parentId === floorId)
      .map(room => ({ id: room.value, title: room.title, value: room.title })),
  ];
}

function generateTableColumns(values: string[] = []) {
  return ['index', 'ticketNumber', ...values];
}

/**
 * Root-scoped so it's reachable from the mobile filters bottom sheet, which
 * Angular opens in its own overlay injector (not a DOM child of the page) —
 * same reason `IncidentsV2TableStore.isLoading` gets manually passed through
 * `MatBottomSheet`'s `data` option instead. See
 * `wiki/wiki/specs/rfc-incidents-criteria-store-migration.md`.
 *
 * Two logically distinct slices, one store (merged 2026-07-30, see
 * `rfc-incidents-v2-filter-store-migration.md`):
 * - `criteria`/`sortCriteria` — the committed, wire-format shape actually sent
 *   to the backend (`IncidentsV2Service`).
 * - everything else — v2's own UI selection state (dropdown options, which
 *   building/floor/room is selected, the column picker, the resolved-date
 *   range), replacing `IncidentsFilterService` (`@services`, `@deprecated`,
 *   v1-only) for every v2 consumer. `isBuildingTable`/`isFloorTable`/
 *   `isRoomTable` and the whole row-scanning option-derivation path that
 *   service carries are intentionally not replicated here — no v2 consumer
 *   ever sets those flags (only v1-embedded contexts do), and v2 only ever
 *   populates options from the backend facets endpoint
 *   (`incidentsControllerV2GetAvailableFilters`), never by scanning a row set.
 * `setCriteria`/`setSortCriteria` stay agnostic of the rest — components
 * translate a UI selection into a criteria patch themselves (id resolution,
 * nulling the other tab's fields), same division of responsibility as before
 * the merge.
 */
export const IncidentsCriteriaStore = signalStore(
  { providedIn: 'root' },
  withState<IncidentsCriteriaState>(createInitialState()),
  withComputed(store => ({
    deviceTypeIds: computed(() => store.deviceTypeOptions().filter(option => option.checked).map(option => option.id!)),
    deviceTypeValue: computed(() => store.deviceTypeOptions().filter(option => option.checked).map(option => option.value)),
    filtersApplied: computed(() => Boolean(
      store.selectedRoom().length ||
      store.selectedFloor().length ||
      store.selectedBuilding().length ||
      store.deviceTypeOptions().some(option => option.checked) ||
      store.search().length,
    )),
    resolvedIncidentsDeviceTypeIds: computed(() => store.resolvedIncidentsDeviceTypeOptions().filter(option => option.checked).map(option => option.id!)),
    resolvedIncidentsDeviceTypeValue: computed(() => store.resolvedIncidentsDeviceTypeOptions().filter(option => option.checked).map(option => option.value)),
    resolvedIncidentsSelectedFilters: computed(() => Boolean(
      store.resolvedIncidentsSelectedBuilding() ||
      store.resolvedIncidentsSelectedFloor() ||
      store.resolvedIncidentsSelectedRoom() ||
      store.resolvedIncidentsDeviceTypeOptions().some(option => option.checked),
    )),
    selectedBuildingId: computed(() => store.buildingOptions().find(({ title }) => title === store.selectedBuilding())?.id),
    selectedFilters: computed(() => Boolean(
      store.selectedBuilding() ||
      store.selectedFloor() ||
      store.selectedRoom() ||
      store.severityOptions().some(option => option.checked) ||
      store.deviceTypeOptions().some(option => option.checked),
    )),
    selectedFloorId: computed(() => store.floorOptions().find(({ value }) => value === store.selectedFloor())?.id),
    selectedRoomId: computed(() => store.roomOptions().find(({ value }) => value === store.selectedRoom())?.id),
    severityFilter: computed(() => store.severityOptions().filter(option => option.checked).map(option => option.value)),
  })),
  withMethods(store => ({
    setCriteria(criteria: Partial<IncidentsFilterCriteria>) {
      patchState(store, s => ({ criteria: { ...s.criteria, ...criteria } }));
    },
    setSortCriteria(sortCriteria: IncidentsSortCriteria | null) {
      patchState(store, { sortCriteria });
    },

    /** Mirrors `IncidentsFilterService.setColumnsValues()`'s tab-driven 'resolved' column toggle, minus the isBuildingTable/isFloorTable/isRoomTable branches no v2 consumer uses. */
    setDisplayedColumns(values: string[]) {
      const isOpen = store.tab() === IncidentTabStatusFilter.OPEN;
      const filtered = isOpen ? values.filter(value => value !== 'resolved') : values;

      patchState(store, { displayedColumns: generateTableColumns(filtered) });
    },

    /** Mirrors `IncidentsFilterService`'s `set incidentTabStatusFilter`/`setColumnsValues()` — toggles the 'resolved' column in/out of both the available options and the currently-active columns on every tab switch. */
    setTab(tab: IncidentTabStatusFilter) {
      if (store.tab() === tab) {
        return;
      }

      const isOpen = tab === IncidentTabStatusFilter.OPEN;
      const columnOptions = isOpen
        ? incidentColumnOptions.filter(({ value }) => value !== 'resolved')
        : incidentColumnOptions;

      const currentColumns = store.displayedColumns();
      let displayedColumns = currentColumns;

      if (isOpen) {
        displayedColumns = currentColumns.filter(column => column !== 'resolved');
      } else if (!currentColumns.includes('resolved')) {
        const reportedIdx = currentColumns.indexOf('reported');

        displayedColumns = reportedIdx !== -1
          ? [...currentColumns.slice(0, reportedIdx + 1), 'resolved', ...currentColumns.slice(reportedIdx + 1)]
          : [...currentColumns, 'resolved'];
      }

      patchState(store, { columnOptions, displayedColumns, search: '', tab });
    },

    setSearch(search: string) {
      patchState(store, { search });
    },

    /** Mirrors `IncidentsFilterService.initOpenIncidentsFilterOptions()` — mirrors the same facets into both the open- and resolved-tab option lists, since v2 never calls `initResolvedIncidents()` (the row-based path that would otherwise own the resolved-tab options). */
    loadFacets(facets: AvailableIncidentFiltersDataDto) {
      const deviceTypeOptions = facets.deviceTypes.map(option => ({ checked: false, id: option.value, title: option.title, value: option.title }));
      const severityOptions = facets.severities.map(severity => ({ checked: false, title: severity.charAt(0).toUpperCase() + severity.slice(1), value: severity }));
      const buildingOptions = [defaultSelectOption, ...facets.buildings.map(option => ({ id: option.value, title: option.title, value: option.title }))];

      patchState(store, {
        buildingOptions,
        deviceTypeOptions,
        facets,
        floorOptions: generateFloorOptions(facets, store.selectedBuilding()),
        resolvedIncidentsBuildingOptions: buildingOptions,
        resolvedIncidentsDeviceTypeOptions: deviceTypeOptions,
        resolvedIncidentsFloorOptions: generateFloorOptions(facets, store.resolvedIncidentsSelectedBuilding()),
        resolvedIncidentsRoomOptions: generateRoomOptions(facets, store.resolvedIncidentsSelectedFloor()),
        roomOptions: generateRoomOptions(facets, store.selectedFloor()),
        severityOptions,
      });
    },

    selectBuilding(value: string) {
      const facets = store.facets();

      patchState(store, {
        floorOptions: facets ? generateFloorOptions(facets, value) : [],
        roomOptions: [],
        selectedBuilding: value,
        selectedFloor: '',
        selectedRoom: '',
      });
    },

    selectFloor(value: string) {
      const facets = store.facets();

      patchState(store, {
        roomOptions: facets ? generateRoomOptions(facets, value) : [],
        selectedFloor: value,
        selectedRoom: '',
      });
    },

    selectRoom(value: string) {
      patchState(store, { selectedRoom: value });
    },

    setDeviceTypeValue(values: string[]) {
      patchState(store, { deviceTypeOptions: store.deviceTypeOptions().map(option => ({ ...option, checked: values.includes(option.value) })) });
    },

    updateDeviceTypeValue(filter: { checked: boolean; value: string; }) {
      patchState(store, {
        deviceTypeOptions: store.deviceTypeOptions().map(option => option.value === filter.value ? { ...option, checked: filter.checked } : option),
      });
    },

    setSeverityFilter(values: string[]) {
      patchState(store, { severityOptions: store.severityOptions().map(option => ({ ...option, checked: values.includes(option.value) })) });
    },

    clearOpenFilters() {
      patchState(store, {
        deviceTypeOptions: store.deviceTypeOptions().map(option => ({ ...option, checked: false })),
        selectedBuilding: '',
        selectedFloor: '',
        selectedRoom: '',
        severityOptions: store.severityOptions().map(option => ({ ...option, checked: false })),
      });
    },

    selectResolvedBuilding(value: string) {
      const facets = store.facets();

      patchState(store, {
        resolvedIncidentsFloorOptions: facets ? generateFloorOptions(facets, value) : [],
        resolvedIncidentsRoomOptions: [],
        resolvedIncidentsSelectedBuilding: value,
        resolvedIncidentsSelectedFloor: '',
        resolvedIncidentsSelectedRoom: '',
      });
    },

    selectResolvedFloor(value: string) {
      const facets = store.facets();

      patchState(store, {
        resolvedIncidentsRoomOptions: facets ? generateRoomOptions(facets, value) : [],
        resolvedIncidentsSelectedFloor: value,
        resolvedIncidentsSelectedRoom: '',
      });
    },

    selectResolvedRoom(value: string) {
      patchState(store, { resolvedIncidentsSelectedRoom: value });
    },

    setResolvedDeviceTypeValue(values: string[]) {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store.resolvedIncidentsDeviceTypeOptions().map(option => ({ ...option, checked: values.includes(option.value) })),
      });
    },

    updateResolvedDeviceTypeValue(filter: { checked: boolean; value: string; }) {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store.resolvedIncidentsDeviceTypeOptions().map(
          option => option.value === filter.value ? { ...option, checked: filter.checked } : option,
        ),
      });
    },

    clearResolvedFilters() {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store.resolvedIncidentsDeviceTypeOptions().map(option => ({ ...option, checked: false })),
        resolvedIncidentsSelectedBuilding: '',
        resolvedIncidentsSelectedFloor: '',
        resolvedIncidentsSelectedRoom: '',
      });
    },

    setResolvedDateRangeId(id: AvsDateRangeIds) {
      patchState(store, { resolvedDateRangeId: id });
    },

    /**
     * Mirrors `IncidentsFilterService.resolvedIncidentsRequest()` — end date
     * pushed one day out to include the whole day, deduped against the
     * current value. Also patches the equivalent fields directly onto
     * `criteria` (the wire-format shape `IncidentsV2Service` actually reads):
     * before the 2026-07-30 store merge this had to go through a component-
     * level `effect()` bridging two separate stores — now that `resolvedDates`
     * and `criteria` live in the same store, there's no cross-store boundary
     * left to bridge, so the store does this in one place instead of every
     * caller re-deriving it.
     */
    setResolvedDateRange(range: DateRange<Date>) {
      if (!range.start || !range.end) {
        return;
      }

      const endDate = new Date(range.end);

      endDate.setDate(endDate.getDate() + 1);

      const startDateTime = new Date(range.start).toISOString();
      const endDateTime = endDate.toISOString();
      const current = store.resolvedDates();

      if (current.startDateTime === startDateTime && current.endDateTime === endDateTime) {
        return;
      }

      patchState(store, s => ({
        criteria: { ...s.criteria, endDateTime, startDateTime },
        resolvedDates: { endDateTime, startDateTime },
      }));
    },

    reset() {
      patchState(store, createInitialState());
    },
  })),
);
